import { useEffect, useMemo, useRef, useState } from "react";
import type {
  CSSProperties,
  KeyboardEvent as ReactKeyboardEvent,
  PointerEvent as ReactPointerEvent,
} from "react";
import { searchZones, zoneFromIana, type ZoneOption } from "./data/zones";
import {
  formatDate,
  formatDayKey,
  formatDifference,
  formatOffsetName,
  formatTime,
  getOffsetSeconds,
  instantAtMinuteOfDay,
  minuteOfDay,
} from "./lib/time";
import {
  MINUTES_PER_DAY,
  sliderPositionPercent,
  TIMELINE_SEGMENTS,
  timelineTickPercent,
} from "./lib/slider";

type TimeFormat = 12 | 24;

type ZoneCard = ZoneOption & {
  id: string;
  editing?: boolean;
  draft?: boolean;
};

type SavedState = {
  cards: ZoneCard[];
  activeId: string;
  timeFormat: TimeFormat;
};

const STORAGE_KEY = "timezones-digital-state-v1";

function createId() {
  return crypto.randomUUID();
}

function createInitialState(): SavedState {
  const detectedZone = Intl.DateTimeFormat().resolvedOptions().timeZone || "Etc/UTC";
  const localZone = zoneFromIana(detectedZone);
  const card = { ...localZone, id: createId() };
  return { cards: [card], activeId: card.id, timeFormat: 12 };
}

function loadState(): SavedState {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return createInitialState();
    const parsed = JSON.parse(stored) as Partial<SavedState>;
    if (!Array.isArray(parsed.cards) || parsed.cards.length === 0) return createInitialState();
    const cards = parsed.cards.filter(
      (card): card is ZoneCard =>
        typeof card?.id === "string" &&
        typeof card?.timeZone === "string" &&
        typeof card?.city === "string" &&
        typeof card?.country === "string",
    );
    if (cards.length === 0) return createInitialState();
    return {
      cards,
      activeId: cards.some(({ id }) => id === parsed.activeId) ? parsed.activeId! : cards[0].id,
      timeFormat: parsed.timeFormat === 24 ? 24 : 12,
    };
  } catch {
    return createInitialState();
  }
}

function Logo() {
  return (
    <svg className="logo" viewBox="0 0 32 32" aria-label="Timezones">
      <circle cx="9" cy="9" r="6" />
      <circle cx="23" cy="9" r="6" />
      <circle cx="9" cy="23" r="6" />
    </svg>
  );
}

function MenuIcon({ open }: { open: boolean }) {
  return open ? (
    <svg viewBox="0 0 20 20" aria-hidden="true">
      <path d="M4 4l12 12M16 4 4 16" />
    </svg>
  ) : (
    <svg viewBox="0 0 20 20" aria-hidden="true">
      <path d="M3 7h14M3 13h14" />
    </svg>
  );
}

function EditIcon() {
  return (
    <svg viewBox="0 0 20 20" aria-hidden="true">
      <path d="m4 14.5-.5 2 2-.5L15.8 5.7l-1.5-1.5L4 14.5Z" />
      <path d="m12.9 5.6 1.5 1.5" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 20 20" aria-hidden="true">
      <path d="M5 5l10 10M15 5 5 15" />
    </svg>
  );
}

function RefreshIcon() {
  return (
    <svg viewBox="0 0 20 20" aria-hidden="true">
      <path d="M15.5 8a6 6 0 1 0 .1 4" />
      <path d="M15.5 4.5V8H12" />
    </svg>
  );
}

function ClockText({ value, live }: { value: string; live: boolean }) {
  const colonIndex = value.indexOf(":");
  if (colonIndex < 0) return value;
  return (
    <>
      {value.slice(0, colonIndex)}
      <span className={live ? "blinking-colon" : ""}>:</span>
      {value.slice(colonIndex + 1)}
    </>
  );
}

function PlusIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

function SliderHandleIcon() {
  return (
    <svg viewBox="0 0 15 7" aria-hidden="true">
      <path d="M4.15.15 4.85.85 2.2 3.5l2.65 2.65-.7.7L.79 3.5 4.15.15Z" />
      <path d="m14.21 3.5-3.36 3.35-.7-.7 2.64-2.65-2.64-2.65.7-.7 3.36 3.35Z" />
      <circle cx="7.5" cy="3.5" r=".5" />
    </svg>
  );
}

function TimeFormatToggle({ value, onChange }: { value: TimeFormat; onChange: (value: TimeFormat) => void }) {
  return (
    <button
      className="format-toggle floating-control"
      type="button"
      aria-label={`Switch to ${value === 12 ? "24" : "12"}-hour time`}
      onClick={() => onChange(value === 12 ? 24 : 12)}
    >
      <span className={`format-toggle__thumb format-toggle__thumb--${value}`} />
      <span className={value === 12 ? "selected" : ""}>12</span>
      <span className={value === 24 ? "selected" : ""}>24</span>
    </button>
  );
}

function Header({
  timeFormat,
  isLive,
  menuOpen,
  onTimeFormatChange,
  onNow,
  onMenu,
}: {
  timeFormat: TimeFormat;
  isLive: boolean;
  menuOpen: boolean;
  onTimeFormatChange: (value: TimeFormat) => void;
  onNow: () => void;
  onMenu: () => void;
}) {
  const localZone = Intl.DateTimeFormat().resolvedOptions().timeZone || "Etc/UTC";
  const [currentTime, setCurrentTime] = useState(() => new Date());
  const localTime = formatTime(currentTime, localZone, timeFormat);

  useEffect(() => {
    const interval = window.setInterval(() => setCurrentTime(new Date()), 1000);
    return () => window.clearInterval(interval);
  }, []);

  return (
    <header className="header">
      <Logo />
      <div className="header__spacer" />
      <button className="now-button floating-control" type="button" onClick={onNow} aria-label="Return to current time">
        {!isLive && <RefreshIcon />}
        <span><ClockText value={localTime.time} live /></span>
        {localTime.period && <span>{localTime.period}</span>}
      </button>
      <TimeFormatToggle value={timeFormat} onChange={onTimeFormatChange} />
      <button
        className="menu-button floating-control"
        type="button"
        aria-label={menuOpen ? "Close information panel" : "Open information panel"}
        aria-expanded={menuOpen}
        onClick={onMenu}
      >
        <MenuIcon open={menuOpen} />
      </button>
    </header>
  );
}

function SearchCard({ card, onSelect, onCancel }: { card: ZoneCard; onSelect: (zone: ZoneOption) => void; onCancel: () => void }) {
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const results = useMemo(() => searchZones(query), [query]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((index) => Math.min(index + 1, results.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((index) => Math.max(index - 1, 0));
    } else if (event.key === "Enter" && results[activeIndex]) {
      event.preventDefault();
      onSelect(results[activeIndex]);
    } else if (event.key === "Escape") {
      onCancel();
    }
  };

  return (
    <section className="card search-card" aria-label="Choose a time zone">
      <div className="search-card__input-row">
        <input
          ref={inputRef}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type city or time zone…"
          aria-label="Search city or time zone"
          aria-controls={`results-${card.id}`}
          aria-activedescendant={results[activeIndex] ? `zone-${card.id}-${activeIndex}` : undefined}
        />
        {query && (
          <button type="button" className="icon-button search-card__clear" onClick={() => setQuery("")} aria-label="Clear search">
            <CloseIcon />
          </button>
        )}
      </div>
      <div className="search-results" id={`results-${card.id}`} role="listbox">
        {results.map((zone, index) => (
          <button
            id={`zone-${card.id}-${index}`}
            className={`search-result ${index === activeIndex ? "is-active" : ""}`}
            type="button"
            role="option"
            aria-selected={index === activeIndex}
            key={zone.timeZone}
            onPointerEnter={() => setActiveIndex(index)}
            onClick={() => onSelect(zone)}
          >
            <span>{zone.city}</span>
            <span className="search-result__meta">{zone.country} · {zone.timeZone}</span>
          </button>
        ))}
        {results.length === 0 && <p className="search-empty">No matching time zone</p>}
      </div>
      <button type="button" className="cancel-button" onClick={onCancel}>Cancel</button>
    </section>
  );
}

const timelineTicks = Array.from({ length: TIMELINE_SEGMENTS + 1 }, (_, index) => index);

function Timeline({ active, timeFormat }: { active: boolean; timeFormat: TimeFormat }) {
  const marks = timeFormat === 24 ? ["00", "06", "12", "18", "00"] : ["12", "6", "12", "6", "12"];
  return (
    <div className="timeline-wrap" aria-hidden="true">
      <div className={`timeline ${active ? "timeline--active" : ""}`}>
        {timelineTicks.map((tick) => (
          <i
            className={`${tick % 4 === 0 ? "whole" : "minor"}${tick === TIMELINE_SEGMENTS ? " last" : ""}`}
            style={{ "--tick-position": `${timelineTickPercent(tick)}%` } as CSSProperties}
            key={tick}
          />
        ))}
      </div>
      <div className={`time-marks ${active ? "time-marks--active" : ""}`}>
        {marks.map((mark, index) => <span key={`${mark}-${index}`}>{mark}</span>)}
      </div>
    </div>
  );
}

function ZoneCardView({
  card,
  active,
  canDelete,
  selectedTime,
  timeFormat,
  baseTimeZone,
  isLive,
  onActivate,
  onEdit,
  onDelete,
  onTimeChange,
  onDragStart,
  onDragOver,
  onDrop,
}: {
  card: ZoneCard;
  active: boolean;
  canDelete: boolean;
  selectedTime: Date;
  timeFormat: TimeFormat;
  baseTimeZone: string;
  isLive: boolean;
  onActivate: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onTimeChange: (instant: Date) => void;
  onDragStart: () => void;
  onDragOver: () => void;
  onDrop: () => void;
}) {
  const { time, period } = formatTime(selectedTime, card.timeZone, timeFormat);
  const date = formatDate(selectedTime, card.timeZone);
  const isSameDay = formatDayKey(selectedTime, card.timeZone) === formatDayKey(selectedTime, baseTimeZone);
  const difference = getOffsetSeconds(selectedTime, card.timeZone) - getOffsetSeconds(selectedTime, baseTimeZone);
  const sliderValue = minuteOfDay(selectedTime, card.timeZone);
  const [sliderFocused, setSliderFocused] = useState(false);
  const [sliderDragging, setSliderDragging] = useState(false);
  const sliderPointerActiveRef = useRef(false);

  const updateFromSlider = (value: number) => {
    onActivate();
    onTimeChange(instantAtMinuteOfDay(selectedTime, card.timeZone, value));
  };

  return (
    <article
      className={`card zone-card ${active ? "zone-card--active" : ""}`}
      style={{ "--slider-position": `${sliderPositionPercent(sliderValue)}%` } as CSSProperties}
      onClick={onActivate}
      draggable={!sliderDragging}
      onDragStart={(event) => {
        if (sliderPointerActiveRef.current || (event.target as Element).closest(".time-slider")) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }
        onDragStart();
      }}
      onDragEnd={onDrop}
      onDragEnter={onDragOver}
      onDragOver={(event) => event.preventDefault()}
      onDrop={onDrop}
    >
      <div className="card__top-row">
        <h2>{card.city}{card.country ? `, ${card.country}` : ""}</h2>
        <div className="card-controls">
          <button className="icon-button" type="button" onClick={(event) => { event.stopPropagation(); onEdit(); }} aria-label={`Edit ${card.city}`}>
            <EditIcon />
          </button>
          {canDelete && (
            <button className="icon-button" type="button" onClick={(event) => { event.stopPropagation(); onDelete(); }} aria-label={`Remove ${card.city}`}>
              <CloseIcon />
            </button>
          )}
        </div>
      </div>
      <p className="zone-offset">{formatOffsetName(selectedTime, card.timeZone)}</p>
      <p className={`zone-date ${isSameDay ? "" : "zone-date--different"}`}>{date}</p>

      <div className="card__bottom">
        <div className="time-row">
          <div className="large-time"><ClockText value={time} live={isLive} /></div>
          {period && <div className="period">{period}</div>}
          {!active && difference !== 0 && (
            <span className={`difference ${difference > 0 ? "difference--ahead" : "difference--behind"}`}>
              {formatDifference(difference)}
            </span>
          )}
        </div>
        <Timeline active={active} timeFormat={timeFormat} />
      </div>

      <div
        className={`slider-indicator ${sliderFocused || sliderDragging ? "slider-indicator--engaged" : ""}`}
        aria-hidden="true"
      >
        <span className="slider-indicator__line" />
        <span className="slider-indicator__handle"><SliderHandleIcon /></span>
      </div>

      <input
        className={`time-slider ${sliderFocused ? "time-slider--focused" : ""}`}
        type="range"
        min="0"
        max={MINUTES_PER_DAY}
        step="1"
        value={sliderValue}
        draggable={false}
        aria-label={`Time in ${card.city}`}
        onClick={(event) => event.stopPropagation()}
        onPointerDown={(event: ReactPointerEvent<HTMLInputElement>) => {
          event.stopPropagation();
          sliderPointerActiveRef.current = true;
          setSliderDragging(true);
          onActivate();
        }}
        onPointerUp={() => {
          sliderPointerActiveRef.current = false;
          setSliderDragging(false);
        }}
        onPointerCancel={() => {
          sliderPointerActiveRef.current = false;
          setSliderDragging(false);
        }}
        onLostPointerCapture={() => {
          sliderPointerActiveRef.current = false;
          setSliderDragging(false);
        }}
        onDragStart={(event) => event.preventDefault()}
        onChange={(event) => updateFromSlider(Number(event.currentTarget.value))}
        onFocus={() => setSliderFocused(true)}
        onBlur={() => {
          sliderPointerActiveRef.current = false;
          setSliderDragging(false);
          setSliderFocused(false);
        }}
      />
    </article>
  );
}

async function sharePage() {
  const data = { title: "Timezones", text: "Compare time zones at a glance", url: window.location.href };
  if (navigator.share) {
    await navigator.share(data);
    return "Shared";
  }
  await navigator.clipboard.writeText(window.location.href);
  return "Link copied";
}

function InfoPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [shareLabel, setShareLabel] = useState("Share this tool");

  const handleShare = async () => {
    try {
      setShareLabel(await sharePage());
      window.setTimeout(() => setShareLabel("Share this tool"), 1800);
    } catch {
      setShareLabel("Couldn’t share");
    }
  };

  return (
    <>
      <button className={`overlay ${open ? "overlay--open" : ""}`} type="button" onClick={onClose} aria-label="Close information panel" tabIndex={open ? 0 : -1} />
      <aside className={`info-panel ${open ? "info-panel--open" : ""}`} aria-hidden={!open}>
        <div className="info-panel__scroll">
          <section className="info-card info-card--install">
            <div>
              <h3>Install Timezones</h3>
              <p>Use your browser menu to add this app to the home screen or dock.</p>
            </div>
            <div className="install-steps" aria-hidden="true">
              <span className="info-icon">↑</span><span>→</span><span className="install-pill">Add to Home Screen <b>＋</b></span>
            </div>
          </section>
          <section className="info-card">
            <div>
              <h3>Made for focus</h3>
              <p>All time calculations and saved cities stay in your browser. No account, analytics, or tracking.</p>
            </div>
            <span className="info-icon" aria-hidden="true">♥</span>
          </section>
          <section className="info-card">
            <div>
              <h3>Take it with you</h3>
              <p>Send the converter to a teammate or open it on another device.</p>
              <button className="dark-pill" type="button" onClick={() => void handleShare()}>{shareLabel}</button>
            </div>
            <span className="info-icon" aria-hidden="true">↗</span>
          </section>
          <div className="info-card-pair">
            <section className="info-card info-card--small"><div><h3>12 / 24</h3><p>Switch formats instantly.</p></div></section>
            <section className="info-card info-card--small"><div><h3>Drag cards</h3><p>Keep your team in order.</p></div></section>
          </div>
        </div>
      </aside>
    </>
  );
}

export default function App() {
  const [initialState] = useState(loadState);
  const [cards, setCards] = useState(initialState.cards);
  const [activeId, setActiveId] = useState(initialState.activeId);
  const [timeFormat, setTimeFormat] = useState<TimeFormat>(initialState.timeFormat);
  const [selectedTime, setSelectedTime] = useState(() => new Date());
  const [isLive, setIsLive] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [draggedId, setDraggedId] = useState<string | null>(null);

  const activeCard = cards.find(({ id }) => id === activeId) ?? cards[0];

  useEffect(() => {
    if (!isLive) return undefined;
    const tick = () => setSelectedTime(new Date());
    tick();
    const interval = window.setInterval(tick, 1000);
    return () => window.clearInterval(interval);
  }, [isLive]);

  useEffect(() => {
    const cleanCards = cards.filter(({ draft }) => !draft).map(({ editing: _editing, draft: _draft, ...card }) => card);
    if (cleanCards.length === 0) return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ cards: cleanCards, activeId, timeFormat } satisfies SavedState));
  }, [cards, activeId, timeFormat]);

  useEffect(() => {
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const updateThemeColor = () => {
      document.querySelector('meta[name="theme-color"]')?.setAttribute("content", media.matches ? "#0c0c0c" : "#f5f6f8");
    };
    updateThemeColor();
    media.addEventListener("change", updateThemeColor);
    return () => media.removeEventListener("change", updateThemeColor);
  }, []);

  const updateCard = (id: string, update: Partial<ZoneCard>) => {
    setCards((current) => current.map((card) => card.id === id ? { ...card, ...update } : card));
  };

  const deleteCard = (id: string) => {
    setCards((current) => {
      const next = current.filter((card) => card.id !== id);
      if (id === activeId && next[0]) setActiveId(next[0].id);
      return next;
    });
  };

  const addCard = () => {
    const fallback = zoneFromIana(activeCard?.timeZone || "Etc/UTC");
    const card: ZoneCard = { ...fallback, id: createId(), draft: true, editing: true };
    setCards((current) => [...current, card]);
  };

  const selectZone = (id: string, zone: ZoneOption) => {
    updateCard(id, { ...zone, editing: false, draft: false });
    setActiveId(id);
  };

  const reorderCards = (targetId: string) => {
    if (!draggedId || draggedId === targetId) return;
    setCards((current) => {
      const from = current.findIndex(({ id }) => id === draggedId);
      const to = current.findIndex(({ id }) => id === targetId);
      if (from < 0 || to < 0) return current;
      const next = [...current];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };

  const changeTime = (time: Date) => {
    setSelectedTime(time);
    setIsLive(false);
  };

  return (
    <main className="app-shell">
      <Header
        timeFormat={timeFormat}
        isLive={isLive}
        menuOpen={menuOpen}
        onTimeFormatChange={setTimeFormat}
        onNow={() => { setSelectedTime(new Date()); setIsLive(true); }}
        onMenu={() => setMenuOpen((open) => !open)}
      />
      <div className={`cards-container ${menuOpen ? "cards-container--panel-open" : ""}`}>
        {cards.map((card, index) => card.editing ? (
          <SearchCard
            key={card.id}
            card={card}
            onSelect={(zone) => selectZone(card.id, zone)}
            onCancel={() => card.draft ? deleteCard(card.id) : updateCard(card.id, { editing: false })}
          />
        ) : (
          <ZoneCardView
            key={card.id}
            card={card}
            active={card.id === activeId}
            canDelete={cards.length > 1 && index > 0}
            selectedTime={selectedTime}
            timeFormat={timeFormat}
            baseTimeZone={activeCard.timeZone}
            isLive={isLive}
            onActivate={() => setActiveId(card.id)}
            onEdit={() => updateCard(card.id, { editing: true })}
            onDelete={() => deleteCard(card.id)}
            onTimeChange={changeTime}
            onDragStart={() => setDraggedId(card.id)}
            onDragOver={() => reorderCards(card.id)}
            onDrop={() => setDraggedId(null)}
          />
        ))}
        <div className="add-card-slot">
          <button className="add-button" type="button" onClick={addCard} aria-label="Add a time zone">
            <PlusIcon />
          </button>
        </div>
      </div>
      <InfoPanel open={menuOpen} onClose={() => setMenuOpen(false)} />
    </main>
  );
}
